import React, { useState, useEffect } from "react";
import { storage, database } from "../../firebaseConfig";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { ref as dbRef, onValue } from "firebase/database";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { PAYMENT_TYPES, getPaymentType, isRecurringFromPaymentType } from "./OfferLanguage";
import "./ServiceForm/ServiceForm.css";

const ServiceForm = ({ 
  onSubmit, 
  initialService = null, 
  isEditing = false,
  onCancel,
  userId
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [category, setCategory] = useState("");
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.ONE_TIME);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [microservices, setMicroservices] = useState([]);
  const [selectedMicroservices, setSelectedMicroservices] = useState([]);
  const [showMicroservices, setShowMicroservices] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link'
  ];

  // Loading microservices from the database
  useEffect(() => {
    if (!userId) return;

    const microservicesRef = dbRef(database, `/World/${userId}/microservices`);
    
    const unsubscribe = onValue(microservicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const microservicesData = snapshot.val();
        const microservicesArray = Object.entries(microservicesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setMicroservices(microservicesArray);
      } else {
        setMicroservices([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Fill form with service data when editing
  useEffect(() => {
    if (initialService) {
      setTitle(initialService.title || "");
      setDescription(initialService.description || "");
      setPrice(initialService.price ? initialService.price.toString() : "");
      setDiscountPercent(initialService.discountPercent ? initialService.discountPercent.toString() : "");
      setCategory(initialService.category || "");
      setPaymentType(getPaymentType(initialService));
      setImageUrl(initialService.imageUrl || "");
      setSelectedMicroservices(initialService.microservices || []);
    } else {
      resetForm();
    }
  }, [initialService]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPrice("");
    setDiscountPercent("");
    setCategory("");
    setPaymentType(PAYMENT_TYPES.ONE_TIME);
    setImageUrl("");
    setImageFile(null);
    setSelectedMicroservices([]);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim() || !description.trim() || !price.trim()) {
      setError("Please fill in the required fields: Title, Description, and Price");
      return;
    }

    if (isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      setError("Please enter a valid price");
      return;
    }

    if (discountPercent && (isNaN(parseFloat(discountPercent)) || parseFloat(discountPercent) < 0 || parseFloat(discountPercent) > 100)) {
      setError("Discount must be between 0 and 100 percent");
      return;
    }

    let finalImageUrl = imageUrl;
    if (imageFile) {
      setIsUploading(true);
      
      try {
        const timestamp = new Date().getTime();
        const fileExtension = imageFile.name.split('.').pop();
        const fileName = `service_images/${title.replace(/\s+/g, '_').toLowerCase()}_${timestamp}.${fileExtension}`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, imageFile);
        finalImageUrl = await getDownloadURL(fileRef);
        
        setIsUploading(false);
        setImageUrl(finalImageUrl);
      } catch (error) {
        console.error("Error uploading image:", error);
        setError("The image could not be uploaded. Please try again.");
        setIsUploading(false);
        return;
      }
    }

    // Extract only important fields from microservices to reduce data size
    const cleanedMicroservices = selectedMicroservices.map(ms => ({
      id: ms.id,
      title: ms.title,
      description: ms.description || '',
      price: ms.price,
      category: ms.category || ''
    }));

    // Create service object
    const serviceData = {
      title: title.trim(),
      description: description.trim(),
      price: parseFloat(price),
      discountPercent: discountPercent ? parseFloat(discountPercent) : 0,
      category: category.trim(),
      paymentType,
      isRecurring: isRecurringFromPaymentType(paymentType),
      imageUrl: finalImageUrl,
      microservices: cleanedMicroservices,
      microservicesTotal: selectedMicroservices.reduce((sum, ms) => sum + ms.price, 0),
      totalPrice: calculateTotalPrice(),
      finalPrice: calculateDiscountedPrice(),
      updatedAt: Date.now()
    };

    if (!isEditing) {
      serviceData.createdAt = Date.now();
    }

    onSubmit(serviceData);
    resetForm();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setError("Please select an image file (jpg, png, etc.)");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setError("The image file is too large (max 5MB)");
        return;
      }
      
      setImageFile(file);
      setError("");
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageUrl(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl("");
    setImageFile(null);
  };

  // Add or remove microservice to/from service
  const handleSelectMicroservice = (microservice) => {
    const isAlreadySelected = selectedMicroservices.some(ms => ms.id === microservice.id);
    
    if (isAlreadySelected) {
      // Remove microservice
      setSelectedMicroservices(prev => prev.filter(ms => ms.id !== microservice.id));
    } else {
      // Add microservice
      setSelectedMicroservices(prev => [...prev, microservice]);
    }
  };

  // Remove microservice from selection
  const handleRemoveMicroservice = (microserviceId) => {
    setSelectedMicroservices(prev => prev.filter(ms => ms.id !== microserviceId));
  };

  // Group microservices by categories
  const getMicroservicesByCategory = () => {
    const categorizedMicroservices = {};
    
    // Ensure microservices without category are also displayed
    microservices.forEach(ms => {
      const category = ms.category ? ms.category.trim() : 'Other';
      if (!categorizedMicroservices[category]) {
        categorizedMicroservices[category] = [];
      }
      categorizedMicroservices[category].push(ms);
    });
    
    return categorizedMicroservices;
  };

  // Calculate total price with microservices
  const calculateTotalPrice = () => {
    const basePrice = parseFloat(price) || 0;
    const microservicePrice = selectedMicroservices.reduce((sum, ms) => sum + ms.price, 0);
    return basePrice + microservicePrice;
  };

  // Calculate discounted price
  const calculateDiscountedPrice = () => {
    const totalPrice = calculateTotalPrice();
    const discount = parseFloat(discountPercent) || 0;
    return totalPrice - (totalPrice * (discount / 100));
  };

  // Format price for display
  const formatPrice = (value) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Get sorted categories for the category filter
  const getSortedCategories = () => {
    const categorized = getMicroservicesByCategory();
    return Object.keys(categorized).sort();
  };

  // Filtered microservices based on the selected category
  const getFilteredMicroservices = () => {
    if (activeCategory === "all") {
      return microservices;
    } else {
      return microservices.filter(ms => {
        const msCategory = ms.category ? ms.category.trim() : 'Other';
        return msCategory === activeCategory;
      });
    }
  };

  return (
    <form className="offer-service-form" onSubmit={handleSubmit}>
      <h3 className="offer-service-form__title">{isEditing ? "Edit Service" : "Create New Service"}</h3>
      
      {error && <div className="offer-service-form__error">{error}</div>}
      
      <div className="offer-service-form__group">
        <label htmlFor="title" className="offer-service-form__label">Title <span className="offer-service-form__required">*</span></label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Website Creation"
          required
          className="offer-service-form__input"
        />
      </div>
      
      <div className="offer-service-form__group">
        <label htmlFor="description" className="offer-service-form__label">Description <span className="offer-service-form__required">*</span></label>
        <ReactQuill
          id="description"
          value={description}
          onChange={setDescription}
          modules={quillModules}
          formats={quillFormats}
          placeholder="Detailed description of the service with formatting options..."
          className="offer-service-form__description-editor"
        />
      </div>
      
      <div className="offer-service-form__row">
        <div className="offer-service-form__group">
          <label htmlFor="price" className="offer-service-form__label">Price (€) <span className="offer-service-form__required">*</span></label>
          <input
            type="number"
            id="price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="99.99"
            min="0"
            step="0.01"
            required
            className="offer-service-form__input"
          />
        </div>
        
        <div className="offer-service-form__group">
          <label htmlFor="discountPercent" className="offer-service-form__label">Discount (%)</label>
          <input
            type="number"
            id="discountPercent"
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
            placeholder="10"
            min="0"
            max="100"
            className="offer-service-form__input"
          />
        </div>
      </div>
      
      <div className="offer-service-form__row">
        <div className="offer-service-form__group">
          <label htmlFor="category" className="offer-service-form__label">Category</label>
          <input
            type="text"
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Web Design, Marketing"
            className="offer-service-form__input"
          />
        </div>
        
        <div className="offer-service-form__group">
          <label className="offer-service-form__label">Payment Type</label>
          <div className="offer-service-form__toggle-container">
            <button
              type="button"
              className={`offer-service-form__toggle-btn ${paymentType === PAYMENT_TYPES.ONE_TIME ? "active" : ""}`}
              onClick={() => setPaymentType(PAYMENT_TYPES.ONE_TIME)}
            >
              One Time
            </button>
            <button
              type="button"
              className={`offer-service-form__toggle-btn ${paymentType === PAYMENT_TYPES.PER_APPOINTMENT ? "active" : ""}`}
              onClick={() => setPaymentType(PAYMENT_TYPES.PER_APPOINTMENT)}
            >
              Per Appointment
            </button>
            <button
              type="button"
              className={`offer-service-form__toggle-btn ${paymentType === PAYMENT_TYPES.RECURRING ? "active" : ""}`}
              onClick={() => setPaymentType(PAYMENT_TYPES.RECURRING)}
            >
              Recurring
            </button>
          </div>
        </div>
      </div>
      
      <div className="offer-service-form__group">
        <label htmlFor="image" className="offer-service-form__label">Image</label>
        <div className="offer-service-form__image-upload-container">
          <input
            type="file"
            id="image"
            onChange={handleImageChange}
            accept="image/*"
            className="offer-service-form__image-upload-input"
          />
          <div className="offer-service-form__image-upload-area" onClick={() => document.getElementById('image').click()}>
            {imageUrl ? (
              <div className="offer-service-form__image-preview-container">
                <img src={imageUrl} alt="Service Preview" className="offer-service-form__image-preview" />
                <button 
                  type="button" 
                  className="offer-service-form__remove-image-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="offer-service-form__image-upload-placeholder">
                <span className="offer-service-form__upload-icon">📷</span>
                <span>Click to upload an image</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {isUploading && (
        <div className="offer-service-form__upload-progress">
          <div className="offer-service-form__progress-bar" style={{ width: `${uploadProgress}%` }}></div>
          <span>Uploading image... {uploadProgress}%</span>
        </div>
      )}

      {/* Microservices section */}
      <div className="offer-service-form__microservices">
        <div className="offer-service-form__microservices-header">
          <h4 className="offer-service-form__microservices-title">Microservices</h4>
          <button 
            type="button" 
            className="offer-service-form__toggle-microservices-btn"
            onClick={() => setShowMicroservices(!showMicroservices)}
          >
            {showMicroservices ? 'Hide' : 'Add Microservices'}
          </button>
        </div>

        {selectedMicroservices.length > 0 && (
          <div className="offer-service-form__selected-microservices">
            <h5 className="offer-service-form__selected-title">Selected Microservices:</h5>
            <div className="offer-service-form__microservices-selected-list">
              {selectedMicroservices.map(ms => (
                <div key={ms.id} className="offer-service-form__microservice-selected-item">
                  <span className="offer-service-form__microservice-selected-title">{ms.title}</span>
                  <button 
                    type="button" 
                    className="offer-service-form__microservice-remove-btn"
                    onClick={() => handleRemoveMicroservice(ms.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="offer-service-form__price-summary">
              <div className="offer-service-form__price-row">
                <span>Base Price:</span>
                <span>{formatPrice(parseFloat(price) || 0)}€</span>
              </div>
              <div className="offer-service-form__price-row">
                <span>Microservices:</span>
                <span>{formatPrice(selectedMicroservices.reduce((sum, ms) => sum + ms.price, 0))}€</span>
              </div>
              {discountPercent && parseFloat(discountPercent) > 0 ? (
                <>
                  <div className="offer-service-form__price-row offer-service-form__price-row--total">
                    <span>Total Price:</span>
                    <span>{formatPrice(calculateTotalPrice())}€</span>
                  </div>
                  <div className="offer-service-form__price-row offer-service-form__price-row--discount">
                    <span>Discount ({discountPercent}%):</span>
                    <span>-{formatPrice(calculateTotalPrice() - calculateDiscountedPrice())}€</span>
                  </div>
                  <div className="offer-service-form__price-row offer-service-form__price-row--final">
                    <span>Final Price:</span>
                    <span>{formatPrice(calculateDiscountedPrice())}€</span>
                  </div>
                </>
              ) : (
                <div className="offer-service-form__price-row offer-service-form__price-row--total">
                  <span>Total Price:</span>
                  <span>{formatPrice(calculateTotalPrice())}€</span>
                </div>
              )}
            </div>
          </div>
        )}

        {showMicroservices && (
          <div className="offer-service-form__microservices-selector">
            <div className="offer-service-form__microservices-categories">
              <button
                type="button"
                className={`offer-service-form__category-btn ${activeCategory === "all" ? "active" : ""}`}
                onClick={() => setActiveCategory("all")}
              >
                All
              </button>
              {getSortedCategories().map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`offer-service-form__category-btn ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="offer-service-form__microservices-list">
              {getFilteredMicroservices().length === 0 ? (
                <p className="offer-service-form__no-microservices">No microservices available. Please create some microservices first.</p>
              ) : (
                getFilteredMicroservices().map(ms => {
                  const isSelected = selectedMicroservices.some(selectedMs => selectedMs.id === ms.id);
                  return (
                    <div
                      key={ms.id}
                      className={`offer-service-form__microservice-item ${isSelected ? "selected" : ""}`}
                      onClick={() => handleSelectMicroservice(ms)}
                    >
                      <div className="offer-service-form__microservice-content">
                        <h5 className="offer-service-form__microservice-title">{ms.title}</h5>
                        {ms.description && <p className="offer-service-form__microservice-description">{ms.description}</p>}
                        <div className="offer-service-form__microservice-details">
                          {ms.category && <span className="offer-service-form__microservice-category">{ms.category}</span>}
                          <span className="offer-service-form__microservice-price">{formatPrice(ms.price)}€</span>
                        </div>
                      </div>
                      <div className="offer-service-form__microservice-selection">
                        <span className={`offer-service-form__selection-indicator ${isSelected ? "selected" : ""}`}>
                          {isSelected ? "✓" : "+"}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="offer-service-form__actions">
        <button type="button" className="offer-service-form__cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button 
          type="submit" 
          className="offer-service-form__submit-btn" 
          disabled={isUploading}
        >
          {isEditing ? "Update" : "Save Service"}
        </button>
      </div>
    </form>
  );
};

export default ServiceForm; 